let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  TextInput,
  Image,
  FlatList,
  Animated,
  Dimensions,
  RefreshControl,
  Modal,
  ActivityIndicator,
  Alert,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  AntDesign,
} from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { UNIFIED_JOB_DATA, getJobsForOffers } from '../../services/UnifiedJobData';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
// FIXED: Safe context imports with fallbacks
let useProfessional, useProfessionalOnboarding, useGeneral;
try {
  const professionalModule = require('../../context/ProfessionalContext');
  useProfessional = professionalModule.useProfessional;
} catch (error) {
  console.warn('⚠️ ProfessionalContext not found, using mock');
  useProfessional = () => ({
    userData: {},
    savedJobs: [],
    jobApplications: [],
    saveJob: async () => {},
    unsaveJob: async () => {},
    trackUserAction: () => {},
  });
}

try {
  const onboardingModule = require('../../context/ProfessionalOnboardingContext');
  useProfessionalOnboarding = onboardingModule.useProfessionalOnboarding;
} catch (error) {
  console.warn('⚠️ ProfessionalOnboardingContext not found, using mock');
  useProfessionalOnboarding = () => ({
    profileData: {},
    calculateJobMatch: () => 75,
    runAIProfileAnalysis: async () => {},
  });
}

try {
  const generalModule = require('../../context/GeneralContext');
  useGeneral = generalModule.useGeneral;
} catch (error) {
  console.warn('⚠️ GeneralContext not found, using mock');
  useGeneral = () => ({
    showToast: () => {},
    trackUserAction: () => {},
  });
}

const { width, height } = Dimensions.get('window');

export default function JobsOffersScreen({ navigation, route }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  // Resolved professional identifiers. The auth flow in this app stores the
  // logged-in user under a few different keys depending on the entry point
  // (login screen, deep link, biometric refresh). We try ALL of them and
  // pull the id/email from whichever wins. Without these, the per-user
  // offers query has no params and returns nothing — that's the bug that
  // was making the screen show only the static UnifiedJobData mock list.
  const [myUserId, setMyUserId] = React.useState(null);
  const [myEmail,  setMyEmail]  = React.useState(null);

  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});

    (async () => {
      // Candidate keys the auth flow / dashboard / profile screen might
      // have written. Order matters — we accept the first one that has
      // both an id and an email, then fall back to partial hits.
      const candidateKeys = [
        'user_data',          // ← what the dashboard's fetchUserProfile reads (line 391)
        'user_profile',       // legacy key
        'auth_user',          // alternative login path
        'userData',           // camelCase variant
        'currentUser',
      ];
      let id = null, email = null;
      for (const k of candidateKeys) {
        try {
          const raw = await AsyncStorage.getItem(k);
          if (!raw) continue;
          const parsed = JSON.parse(raw);
          // Some keys wrap the user under { user: {...} } — handle both shapes
          const u = parsed?.user || parsed;
          if (!id    && (u?.id || u?.user_id))      id    = u.id || u.user_id;
          if (!email && u?.email)                   email = u.email;
          if (id && email) break;
        } catch {}
      }
      // Final fallbacks — stable global keys some flows write directly
      if (!email) email = await AsyncStorage.getItem('user_email').catch(() => null);
      if (!id)    id    = await AsyncStorage.getItem('user_id').catch(() => null);

      console.log('[JobsOffers] resolved identifiers — id:', id, 'email:', email);
      if (id)    setMyUserId(id);
      if (email) setMyEmail(email);
    })();
  }, []);

  // Offers fetched from /api/data/job_offers, filtered to this user.
  // Replaces the static UnifiedJobData source so the recruiter's
  // JobOfferManagementScreen POSTs actually reach the professional here.
  const [dbOffers, setDbOffers] = React.useState([]);

  // Fetch offers filtered to this professional. Two queries (user_id then
  // candidate_email) because legacy offers may have only one populated.
  // De-dupe by id; transform into the UI shape the rest of the screen reads.
  const loadOffers = React.useCallback(async () => {
    if (!myUserId && !myEmail) {
      console.log('[JobsOffers] loadOffers: skipped — no user identifier yet');
      return;
    }
    try {
      const queries = [];
      if (myUserId) queries.push(`/api/data/job_offers?user_id=${myUserId}&limit=100`);
      if (myEmail)  queries.push(`/api/data/job_offers?candidate_email=${encodeURIComponent(myEmail)}&limit=100`);
      console.log('[JobsOffers] firing queries:', queries);

      const results = await Promise.all(queries.map(q =>
        apiFetch(q).catch((e) => { console.warn('[JobsOffers] query failed:', q, e?.message); return { data: [] }; })
      ));
      const merged = [];
      const seen = new Set();
      for (const r of results) {
        const arr = Array.isArray(r) ? r : (r?.data || r?.results || []);
        for (const o of arr) {
          if (!o?.id || seen.has(o.id)) continue;
          seen.add(o.id);
          merged.push(o);
        }
      }
      console.log('[JobsOffers] merged DB offers:', merged.length, merged.map(o => ({ id: o.id, status: o.status })));

      // Transform DB row → UI shape (title, company, location, salary, status, etc.)
      const ui = merged.map(o => {
        const salaryNum = Number(o.offered_salary || o.salary) || 0;
        const cur = o.salary_currency || 'GBP';
        const salarySym = cur === 'GBP' ? '£' : cur === 'USD' ? '$' : cur === 'EUR' ? '€' : '';
        const offerStatus = (o.status || '').toLowerCase();
        // Map raw offer status → UI status enum used by the screen
        const uiStatus =
          offerStatus === 'accepted' ? 'offer_accepted' :
          offerStatus === 'rejected' || offerStatus === 'declined' ? 'rejected' :
          offerStatus === 'sent' || offerStatus === 'pending' ? 'offer_received' :
          'offer_received';
        return {
          id:           o.id,
          offerId:      o.id,
          jobId:        o.job_id,
          applicationId: o.job_application_id || null,
          title:        o.position || 'Position',
          company:      o.company_name || o.employer_name || '',
          location:     o.location || '',
          salary:       salaryNum ? `${salarySym}${salaryNum.toLocaleString()}` : '',
          description:  o.notes || o.benefits || '',
          requirements: [],
          type:         o.contract_type || 'Permanent',
          experience:   '',
          remote:       (o.work_type || '').toLowerCase().includes('remote'),
          posted:       o.offer_date || o.created_at || new Date().toISOString(),
          appliedDate:  o.created_at,
          deadline:     o.deadline || null,
          startDate:    o.start_date || null,
          matchScore:   90,
          status:       uiStatus,
          rawOffer:     o,
        };
      });
      setDbOffers(ui);
      // Cache for offline
      const _ck = `professional_${myUserId || myEmail}_jobsoffersscreen`;
      AsyncStorage.setItem(_ck, JSON.stringify(ui)).catch(() => {});
    } catch (e) { console.warn('JobsOffers load:', e?.message); }
  }, [myUserId, myEmail]);

  React.useEffect(() => { loadOffers(); }, [loadOffers]);

  // INTEGRATED: Multiple context hooks for comprehensive data access
  const { 
    userData = {}, 
    savedJobs = [], 
    jobApplications = [], 
    saveJob,
    unsaveJob,
    trackUserAction 
  } = useProfessional() || {};
  
  const {
    profileData = {},
    calculateJobMatch,
    runAIProfileAnalysis
  } = useProfessionalOnboarding() || {};
  
  const { showToast, trackUserAction: trackGeneralAction } = useGeneral() || {};

  // States
  const [activeTab, setActiveTab] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    jobType: [],
    location: [],
    experience: [],
    remote: false,
    salary: { min: '', max: '' },
  });
  const [sortBy, setSortBy] = useState('relevance');

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  // UNIFIED: Use consistent job data across all screens
  // Database-fetched offers take precedence so the recruiter's just-sent
  // offer shows up. The legacy UnifiedJobData source remains as a fallback
  // for jobs the user has applied to / saved but doesn't have an offer for.
  const staticJobs = getJobsForOffers();
  const jobOffers = React.useMemo(() => {
    // De-dupe — if a static job has the same id as a DB offer, prefer the DB one
    const dbIds = new Set(dbOffers.map(o => String(o.id)));
    const onlyStaticThatAreNotOffers = (staticJobs || []).filter(j => !dbIds.has(String(j.id)));
    return [...dbOffers, ...onlyStaticThatAreNotOffers];
  }, [dbOffers, staticJobs]);

  // Tab options
  const tabOptions = [
    { key: 'All', label: 'All Jobs', icon: 'briefcase-outline' },
    { key: 'Recommended', label: 'For You', icon: 'star-outline' },
    { key: 'Remote', label: 'Remote', icon: 'home-outline' },
    { key: 'Recent', label: 'Recent', icon: 'time-outline' },
    { key: 'Saved', label: 'Saved', icon: 'heart-outline' },
    { key: 'Applied', label: 'Applied', icon: 'checkmark-circle-outline' },
  ];

  // Sort options
  const sortOptions = [
    { key: 'relevance', label: 'Most Relevant', icon: 'star-outline' },
    { key: 'date', label: 'Most Recent', icon: 'time-outline' },
    { key: 'salary', label: 'Highest Salary', icon: 'trending-up-outline' },
    { key: 'match', label: 'Best Match', icon: 'target-outline' },
  ];

  // Initialize animations
  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  // UNIFIED: Enhanced navigation to OfferDetails with consistent data
  const navigateToOfferDetails = (job) => {
    try {
      console.log('🚀 UNIFIED NAVIGATION: Starting OfferDetails navigation');
      console.log('📋 Job data:', JSON.stringify(job, null, 2));

      // Use unified data structure for consistent navigation
      const offerDetailsParams = {
        // Primary identifiers
        offerId: job.offerId || job.id,
        id: job.id,
        jobId: job.jobId || job.id,
        
        // Complete unified job object
        offer: job,
        job: job,
        data: job,
        
        // Navigation metadata
        source: 'JobsOffers',
        timestamp: new Date().toISOString(),
      };

      console.log('📤 UNIFIED PARAMS:', JSON.stringify(offerDetailsParams, null, 2));

      // Navigate to OfferDetails
      navigation.navigate('OfferDetails', offerDetailsParams);
      
      // Track successful navigation
      if (trackUserAction && typeof trackUserAction === 'function') {
        trackUserAction('offer_details_navigation_success', {
          offerId: job.offerId || job.id,
          jobTitle: job.title,
          company: job.company,
        });
      }

    } catch (error) {
      console.error('❌ UNIFIED NAVIGATION ERROR:', error);
      
      // Track navigation failure
      if (trackUserAction && typeof trackUserAction === 'function') {
        trackUserAction('offer_details_navigation_error', {
          offerId: job.offerId || job.id,
          error: error.message,
        });
      }
      
      // Show user-friendly error
      Alert.alert(
        'Navigation Error',
        `Unable to open offer details: ${error.message}`,
        [
          { 
            text: 'Try Again', 
            onPress: () => navigateToOfferDetails(job) 
          },
          { 
            text: 'Go to Applications', 
            onPress: () => {
              try {
                navigation.navigate('JobApplications');
              } catch (navError) {
                navigation.navigate('Applications');
              }
            }
          },
          { 
            text: 'Cancel', 
            style: 'cancel' 
          }
        ]
      );
    }
  };

  // INTEGRATED: Navigate to applications screen
  const navigateToApplications = () => {
    try {
      console.log('🧭 Navigating to JobApplications');
      navigation.navigate('JobApplications');
    } catch (error) {
      try {
        navigation.navigate('Applications');
      } catch (secondError) {
        console.error('❌ Error navigating to applications:', error);
        Alert.alert('Error', 'Unable to open applications screen.');
      }
    }
  };

  // Toggle job save status
  const toggleSaveJob = async (jobId) => {
    try {
      const isSaved = savedJobs.includes(jobId);
      
      if (isSaved) {
        if (unsaveJob && typeof unsaveJob === 'function') {
          await unsaveJob(jobId);
        }
        if (showToast && typeof showToast === 'function') {
          showToast('Job removed from saved jobs', 'success');
        }
      } else {
        if (saveJob && typeof saveJob === 'function') {
          await saveJob(jobId);
        }
        if (showToast && typeof showToast === 'function') {
          showToast('Job saved successfully', 'success');
        }
      }
      
      // Track action
      if (trackUserAction && typeof trackUserAction === 'function') {
        trackUserAction(isSaved ? 'job_unsaved' : 'job_saved', { jobId });
      }
    } catch (error) {
      console.error('Error toggling job save status:', error);
      if (showToast && typeof showToast === 'function') {
        showToast('Failed to update saved jobs', 'error');
      }
    }
  };

  // Get filtered and sorted jobs
  const getFilteredJobs = () => {
    let filtered = [...jobOffers];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(job =>
        String(job.title ?? '').toLowerCase().includes(query) ||
        String(job.company ?? '').toLowerCase().includes(query) ||
        String(job.location ?? '').toLowerCase().includes(query) ||
        String(job.description ?? '').toLowerCase().includes(query) ||
        job.requirements.some(req => req.toLowerCase().includes(query))
      );
    }

    // Apply tab filter
    switch (activeTab) {
      case 'Recommended':
        filtered = filtered.filter(job => job.matchScore >= 85);
        break;
      case 'Remote':
        filtered = filtered.filter(job => job.remote);
        break;
      case 'Recent':
        const threeDaysAgo = new Date();
        threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
        filtered = filtered.filter(job => new Date(job.posted) >= threeDaysAgo);
        break;
      case 'Saved':
        filtered = filtered.filter(job => savedJobs.includes(job.id));
        break;
      case 'Applied':
        // Show jobs that have been applied to (have appliedDate)
        filtered = filtered.filter(job => job.appliedDate);
        break;
    }

    // Apply additional filters
    if (selectedFilters.jobType.length > 0) {
      filtered = filtered.filter(job => selectedFilters.jobType.includes(job.type));
    }

    if (selectedFilters.location.length > 0) {
      filtered = filtered.filter(job => {
        const jobCity = job.location.split(',')[0].trim();
        return selectedFilters.location.includes(jobCity);
      });
    }

    if (selectedFilters.experience.length > 0) {
      filtered = filtered.filter(job => selectedFilters.experience.includes(job.experience));
    }

    if (selectedFilters.remote) {
      filtered = filtered.filter(job => job.remote);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.posted) - new Date(a.posted);
        case 'salary':
          const aSalary = parseInt(a.salary.replace(/[^0-9]/g, '')) || 0;
          const bSalary = parseInt(b.salary.replace(/[^0-9]/g, '')) || 0;
          return bSalary - aSalary;
        case 'match':
          return b.matchScore - a.matchScore;
        case 'relevance':
        default:
          // Featured jobs first, then by match score
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          return b.matchScore - a.matchScore;
      }
    });

    return filtered;
  };

  // Get tab count
  const getTabCount = (tabKey) => {
    switch (tabKey) {
      case 'All':
        return jobOffers.length;
      case 'Recommended':
        return jobOffers.filter(job => job.matchScore >= 85).length;
      case 'Remote':
        return jobOffers.filter(job => job.remote).length;
      case 'Recent':
        const threeDaysAgo = new Date();
        threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
        return jobOffers.filter(job => new Date(job.posted) >= threeDaysAgo).length;
      case 'Saved':
        return savedJobs.length;
      case 'Applied':
        return jobOffers.filter(job => job.appliedDate).length;
      default:
        return 0;
    }
  };

  // Handle refresh
  const onRefresh = async () => {
    setRefreshing(true);
    try {
      // Re-fetch the user's offers from the DB so any new offers from the
      // recruiter's JobOfferManagementScreen show up.
      await loadOffers();
      if (trackUserAction && typeof trackUserAction === 'function') {
        trackUserAction('job_offers_refreshed', {});
      }
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
      
      if (diffInHours < 1) return 'Just posted';
      if (diffInHours < 24) return `${diffInHours}h ago`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) return `${diffInDays}d ago`;
      
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    } catch (error) {
      return 'Recently posted';
    }
  };

  // Render header
  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <Ionicons name="arrow-back" size={24} color="#ffffff" />
      </TouchableOpacity>
      
      <View style={styles.headerTitleContainer}>
        <Text style={styles.headerTitle}>Job Offers</Text>
        <Text style={styles.headerSubtitle}>{getFilteredJobs().length} opportunities</Text>
      </View>
      
      <View style={styles.headerActions}>
        <TouchableOpacity
          style={styles.headerActionButton}
          onPress={() => setShowFilterModal(true)}
        >
          <Ionicons name="options-outline" size={20} color="#ffffff" />
        </TouchableOpacity>
        
        <TouchableOpacity
          style={styles.headerActionButton}
          onPress={navigateToApplications}
        >
          <Ionicons name="document-text-outline" size={20} color="#ffffff" />
        </TouchableOpacity>
      </View>
    </View>
  );

  // Render search bar
  const renderSearchBar = () => (
    <View style={styles.searchContainer}>
      <View style={styles.searchBar}>
        <Ionicons name="search" size={20} color="#64748B" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search jobs, companies, skills..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholderTextColor="#94a3b8"
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <Ionicons name="close-circle" size={20} color="#64748B" />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );

  // Render statistics
  const renderStatistics = () => {
    const allJobs = jobOffers.length;
    const recommendedJobs = jobOffers.filter(job => job.matchScore >= 85).length;
    const remoteJobs = jobOffers.filter(job => job.remote).length;
    const appliedJobs = jobOffers.filter(job => job.appliedDate).length;

    return (
      <View style={styles.statsContainer}>
        <ScrollView 
          horizontal 
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.statsScrollContent}
        >
          <View style={[styles.statCard, styles.primaryStatCard]}>
            <Ionicons name="briefcase" size={24} color="#0e4e96" />
            <Text style={styles.statNumber}>{allJobs}</Text>
            <Text style={styles.statLabel}>Total Jobs</Text>
          </View>
          
          <View style={[styles.statCard, styles.successStatCard]}>
            <Ionicons name="star" size={24} color="#10b981" />
            <Text style={[styles.statNumber, { color: '#10b981' }]}>{recommendedJobs}</Text>
            <Text style={styles.statLabel}>For You</Text>
          </View>
          
          <View style={[styles.statCard, styles.infoStatCard]}>
            <Ionicons name="home" size={24} color="#06b6d4" />
            <Text style={[styles.statNumber, { color: '#06b6d4' }]}>{remoteJobs}</Text>
            <Text style={styles.statLabel}>Remote</Text>
          </View>
          
          <TouchableOpacity 
            style={[styles.statCard, styles.warningStatCard]}
            onPress={navigateToApplications}
          >
            <Ionicons name="document-text" size={24} color="#f59e0b" />
            <Text style={[styles.statNumber, { color: '#f59e0b' }]}>{appliedJobs}</Text>
            <Text style={styles.statLabel}>Applied</Text>
          </TouchableOpacity>
        </ScrollView>
      </View>
    );
  };

  // Render tabs
  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabsScrollContent}
      >
        {tabOptions.map((tab) => {
          const count = getTabCount(tab.key);
          return (
            <TouchableOpacity
              key={tab.key}
              style={[
                styles.tab,
                activeTab === tab.key && styles.activeTab,
              ]}
              onPress={() => setActiveTab(tab.key)}
            >
              <View style={styles.tabContent}>
                <Ionicons 
                  name={tab.icon} 
                  size={16} 
                  color={activeTab === tab.key ? '#0e4e96' : '#64748B'} 
                />
                <Text
                  style={[
                    styles.tabText,
                    activeTab === tab.key && styles.activeTabText,
                  ]}
                >
                  {tab.label}
                </Text>
                {count > 0 && (
                  <View style={[
                    styles.tabBadge,
                    activeTab === tab.key && styles.activeTabBadge,
                  ]}>
                    <Text style={[
                      styles.tabBadgeText,
                      activeTab === tab.key && styles.activeTabBadgeText,
                    ]}>
                      {count}
                    </Text>
                  </View>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );

  // Render job card
  const renderJobCard = ({ item }) => {
    const isSaved = savedJobs.includes(item.id);
    const isApplied = item.appliedDate; // Check if job has been applied to

    return (
      <Animated.View
        style={[
          styles.jobCard,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        <TouchableOpacity
          style={styles.jobCardContent}
          onPress={() => navigateToOfferDetails(item)}
          onLongPress={() => {
            // Long press for debug info
            Alert.alert(
              'Debug Info',
              `Job ID: ${item.id}\nOffer ID: ${item.offerId}\nApplication ID: ${item.applicationId}\nJob Title: ${item.title}\nCompany: ${item.company}\nStatus: ${item.status}`,
              [
                { text: 'View Offer Details', onPress: () => navigateToOfferDetails(item) },
                { text: 'Applications', onPress: navigateToApplications },
                { text: 'Cancel', style: 'cancel' }
              ]
            );
          }}
        >
          {/* Featured/Urgent indicators */}
          {item.featured && (
            <View style={styles.featuredBadge}>
              <Ionicons name="star" size={12} color="#ffffff" />
              <Text style={styles.featuredBadgeText}>Featured</Text>
            </View>
          )}
          
          {item.urgent && (
            <View style={styles.urgentBadge}>
              <Ionicons name="flash" size={12} color="#ffffff" />
              <Text style={styles.urgentBadgeText}>Urgent</Text>
            </View>
          )}

          <View style={styles.jobCardHeader}>
            <View style={styles.companyLogoContainer}>
              {item.companyLogo ? (
                <Image 
                  source={{ uri: (item.companyLogo && typeof item.companyLogo==='string' && item.companyLogo.startsWith('http')) ? item.companyLogo : undefined }} 
                  style={styles.companyLogo}
                  onError={() => console.warn('Failed to load logo')}
                />
              ) : (
                <View style={styles.companyLogoPlaceholder}>
                  <Text style={styles.companyLogoPlaceholderText}>
                    {item.company.charAt(0)}
                  </Text>
                </View>
              )}
            </View>

            <View style={styles.jobCardHeaderInfo}>
              <Text style={styles.jobTitle} numberOfLines={2}>
                {item.title}
              </Text>
              <Text style={styles.companyName} numberOfLines={1}>
                {item.company}
              </Text>
              <View style={styles.jobMetadata}>
                <View style={styles.metadataItem}>
                  <Ionicons name="location-outline" size={14} color="#64748B" />
                  <Text style={styles.metadataText}>{item.location}</Text>
                </View>
                <View style={styles.metadataItem}>
                  <Ionicons name="time-outline" size={14} color="#64748B" />
                  <Text style={styles.metadataText}>{formatRelativeTime(item.posted)}</Text>
                </View>
              </View>
            </View>

            <TouchableOpacity
              style={styles.saveButton}
              onPress={() => toggleSaveJob(item.id)}
            >
              <Ionicons
                name={isSaved ? "heart" : "heart-outline"}
                size={20}
                color={isSaved ? "#ef4444" : "#64748B"}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.jobCardBody}>
            <View style={styles.jobTags}>
              <View style={[styles.jobTag, styles.typeTag]}>
                <Text style={styles.jobTagText}>{item.type}</Text>
              </View>
              
              {item.remote && (
                <View style={[styles.jobTag, styles.remoteTag]}>
                  <Ionicons name="home" size={12} color="#06b6d4" />
                  <Text style={[styles.jobTagText, { color: '#06b6d4' }]}>Remote</Text>
                </View>
              )}
              
              <View style={[styles.jobTag, styles.experienceTag]}>
                <Text style={styles.jobTagText}>{item.experience}</Text>
              </View>
              
              {/* Application status indicator */}
              {isApplied && (
                <View style={[styles.jobTag, styles.appliedTag]}>
                  <Ionicons name="checkmark" size={12} color="#10b981" />
                  <Text style={[styles.jobTagText, { color: '#10b981' }]}>Applied</Text>
                </View>
              )}
            </View>

            <Text style={styles.jobDescription} numberOfLines={2}>
              {item.description}
            </Text>

            <View style={styles.jobFooter}>
              <View style={styles.salaryContainer}>
                <Ionicons name="cash-outline" size={16} color="#10b981" />
                <Text style={styles.salaryText}>{item.salary}</Text>
              </View>

              <View style={styles.matchScoreContainer}>
                <View style={[styles.matchScore, { backgroundColor: item.matchScore >= 85 ? '#10b981' : item.matchScore >= 70 ? '#f59e0b' : '#64748B' }]}>
                  <Text style={styles.matchScoreText}>{item.matchScore}%</Text>
                </View>
                <Text style={styles.matchLabel}>Match</Text>
              </View>
            </View>

            {/* Application status details */}
            {isApplied && (
              <View style={styles.applicationStatus}>
                <View style={styles.statusIndicator}>
                  <Ionicons 
                    name={
                      item.status === 'offer_received' ? 'gift-outline' :
                      item.status === 'interview_scheduled' ? 'calendar-outline' :
                      item.status === 'interview_completed' ? 'checkmark-circle-outline' :
                      item.status === 'under_review' ? 'eye-outline' :
                      item.status === 'rejected' ? 'close-circle-outline' :
                      'document-text-outline'
                    } 
                    size={16} 
                    color={
                      item.status === 'offer_received' ? '#10b981' :
                      item.status === 'interview_scheduled' ? '#0e4e96' :
                      item.status === 'interview_completed' ? '#10b981' :
                      item.status === 'under_review' ? '#f59e0b' :
                      item.status === 'rejected' ? '#ef4444' :
                      '#64748B'
                    } 
                  />
                  <Text style={[styles.statusText, {
                    color: 
                      item.status === 'offer_received' ? '#10b981' :
                      item.status === 'interview_scheduled' ? '#0e4e96' :
                      item.status === 'interview_completed' ? '#10b981' :
                      item.status === 'under_review' ? '#f59e0b' :
                      item.status === 'rejected' ? '#ef4444' :
                      '#64748B'
                  }]}>
                    {item.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Text>
                </View>
                
                {item.interviewDate && item.status === 'interview_scheduled' && (
                  <Text style={styles.interviewDate}>
                    Interview: {new Date(item.interviewDate).toLocaleDateString('en-GB')}
                  </Text>
                )}

                {/* Accept / Decline — only shown when there's an actual
                    offer to respond to. Status flips to offer_accepted /
                    rejected after the response, hiding these buttons. */}
                {item.status === 'offer_received' && item.offerId ? (
                  <View style={{ flexDirection: 'row', gap: 8, marginTop: 12 }}>
                    <TouchableOpacity
                      style={{ flex: 1, backgroundColor: '#10b981', paddingVertical: 10, borderRadius: 8, alignItems: 'center', flexDirection: 'row', justifyContent: 'center' }}
                      onPress={() => respondToOffer(item, true)}
                    >
                      <Ionicons name="checkmark-circle" size={16} color="#fff" />
                      <Text style={{ color: '#fff', fontWeight: '600', marginLeft: 6 }}>Accept</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={{ flex: 1, backgroundColor: '#ef4444', paddingVertical: 10, borderRadius: 8, alignItems: 'center', flexDirection: 'row', justifyContent: 'center' }}
                      onPress={() => respondToOffer(item, false)}
                    >
                      <Ionicons name="close-circle" size={16} color="#fff" />
                      <Text style={{ color: '#fff', fontWeight: '600', marginLeft: 6 }}>Decline</Text>
                    </TouchableOpacity>
                  </View>
                ) : null}

                {item.status === 'offer_accepted' && (
                  <View style={{ marginTop: 10, backgroundColor: '#10b98115', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6, alignSelf: 'flex-start', flexDirection: 'row', alignItems: 'center' }}>
                    <Ionicons name="checkmark-done" size={14} color="#10b981" />
                    <Text style={{ color: '#10b981', fontSize: 12, fontWeight: '600', marginLeft: 4 }}>You accepted this offer</Text>
                  </View>
                )}
              </View>
            )}
          </View>
        </TouchableOpacity>
      </Animated.View>
    );
  };

  // ── Accept / Decline an offer ────────────────────────────────────────
  // When the professional accepts an offer, two things must happen so the
  // business side picks it up:
  //   1. job_offers.status flips to 'accepted' (+ accepted_date) — the
  //      recruiter's AcceptedOffersScreen filters by status='accepted'
  //   2. The candidate's applications row stage bumps to 'offer_accepted' —
  //      so the pipeline view reflects the candidate moving forward to the
  //      hire stage
  // Decline mirrors the same pattern with 'rejected' / 'declined'.
  const respondToOffer = async (item, accept) => {
    if (!item?.offerId && !item?.id) {
      Alert.alert('No offer', 'This card has no offer id.');
      return;
    }
    const offerId = item.offerId || item.id;
    const today   = new Date().toISOString().slice(0, 10);
    const nowIso  = new Date().toISOString();
    const verb    = accept ? 'Accept' : 'Decline';
    Alert.alert(
      `${verb} offer`,
      `${verb} the offer for ${item.title}${item.company ? ' at ' + item.company : ''}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: verb,
          style: accept ? 'default' : 'destructive',
          onPress: async () => {
            // 1. Update the offer row
            const payload = accept
              ? { status: 'accepted', accepted_date: today, response_date: nowIso }
              : { status: 'rejected', declined_date: today, response_date: nowIso };
            try {
              await apiFetch(`/api/data/job_offers/${offerId}`, {
                method: 'PUT', body: JSON.stringify(payload),
              });
            } catch (e) { console.warn('respondToOffer offer:', e?.message); }

            // 2. Bump applicant stage so the recruiter pipeline reflects it.
            //    Try the offer's application_id first (job_application_id),
            //    then fall back to looking up by candidate_email.
            let appId = item.rawOffer?.job_application_id || item.applicationId || null;
            if (!appId && (myEmail || item.rawOffer?.candidate_email)) {
              const email = item.rawOffer?.candidate_email || myEmail;
              try {
                const r = await apiFetch(`/api/data/applications?candidate_email=${encodeURIComponent(email)}&limit=20`);
                const rows = Array.isArray(r) ? r : (r?.data || []);
                const match = rows.find(a => item.rawOffer?.job_id && a.job_id === item.rawOffer.job_id) || rows[0];
                appId = match?.id || null;
              } catch {}
            }
            if (appId) {
              try {
                await apiFetch(`/api/recruitment/applicant/${appId}/stage`, {
                  method: 'POST',
                  body: JSON.stringify({
                    stage:  accept ? 'offer_accepted' : 'rejected',
                    status: accept ? 'accepted'        : 'declined',
                    note:   accept ? `Candidate accepted offer for ${item.title}` : `Candidate declined offer for ${item.title}`,
                  }),
                });
              } catch {}
            }

            // 3. Mirror locally so the card updates immediately
            setDbOffers(prev => prev.map(o =>
              o.id === offerId
                ? { ...o, status: accept ? 'offer_accepted' : 'rejected', rawOffer: { ...o.rawOffer, ...payload } }
                : o
            ));

            Alert.alert(
              accept ? 'Offer accepted' : 'Offer declined',
              accept
                ? "We've notified the employer. They'll be in touch about next steps."
                : "We've notified the employer."
            );
          },
        },
      ]
    );
  };

  // ── Render ────────────────────────────────────────────────────────────

  // Render empty state
  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <Ionicons name="briefcase-outline" size={64} color="#d1d5db" />
      <Text style={styles.emptyStateTitle}>No Jobs Found</Text>
      <Text style={styles.emptyStateMessage}>
        {searchQuery.trim() 
          ? `No jobs match "${searchQuery}". Try adjusting your search.`
          : activeTab === 'All'
          ? "No job offers available at the moment."
          : `No ${activeTab.toLowerCase()} jobs found.`
        }
      </Text>
      <TouchableOpacity 
        style={styles.refreshButton}
        onPress={onRefresh}
      >
        <Text style={styles.refreshButtonText}>Refresh</Text>
      </TouchableOpacity>
    </View>
  );

  if (isLoading) {
    return (
      <SafeAreaView style={styles.loadingContainer}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        <View style={styles.loadingContent}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading job offers...</Text>
        </View>
      </SafeAreaView>
    );
  }

  const filteredJobs = getFilteredJobs();

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      
      <Animated.View style={[styles.content, { opacity: fadeAnim }]}>
        {renderHeader()}
        {renderSearchBar()}
        {renderStatistics()}
        {renderTabs()}

        {filteredJobs.length === 0 ? (
          renderEmptyState()
        ) : (
          <FlatList
            data={filteredJobs}
            renderItem={renderJobCard}
            keyExtractor={(item) => item.id}
            style={styles.jobsList}
            contentContainerStyle={styles.jobsListContent}
            showsVerticalScrollIndicator={false}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                colors={['#0e4e96']}
                tintColor="#0e4e96"
              />
            }
          />
        )}
      </Animated.View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  content: {
    flex: 1,
  },
  header: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 16,
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 8,
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#ffffff',
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#ffffff',
    opacity: 0.8,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerActionButton: {
    padding: 8,
    marginLeft: 8,
  },
  searchContainer: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#1f2937',
    marginLeft: 12,
  },
  statsContainer: {
    backgroundColor: '#ffffff',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  statsScrollContent: {
    paddingHorizontal: 16,
  },
  statCard: {
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    marginRight: 16,
    borderRadius: 12,
    minWidth: 80,
  },
  primaryStatCard: {
    backgroundColor: '#0e4e96' + '20',
  },
  successStatCard: {
    backgroundColor: '#10b981' + '20',
  },
  infoStatCard: {
    backgroundColor: '#06b6d4' + '20',
  },
  warningStatCard: {
    backgroundColor: '#f59e0b' + '20',
  },
  statNumber: {
    fontSize: 20,
    fontWeight: '700',
    color: '#0e4e96',
    marginTop: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#64748B',
    fontWeight: '500',
    marginTop: 2,
  },
  tabsContainer: {
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  tabsScrollContent: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  tab: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 12,
    borderRadius: 20,
    backgroundColor: '#f3f4f6',
  },
  activeTab: {
    backgroundColor: '#0e4e96' + '20',
  },
  tabContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
    marginLeft: 6,
  },
  activeTabText: {
    color: '#0e4e96',
  },
  tabBadge: {
    backgroundColor: '#e5e7eb',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 6,
    minWidth: 20,
    alignItems: 'center',
  },
  activeTabBadge: {
    backgroundColor: '#0e4e96',
  },
  tabBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#64748B',
  },
  activeTabBadgeText: {
    color: '#ffffff',
  },
  jobsList: {
    flex: 1,
  },
  jobsListContent: {
    padding: 16,
  },
  jobCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  jobCardContent: {
    padding: 16,
  },
  featuredBadge: {
    position: 'absolute',
    top: 12,
    right: 12,
    backgroundColor: '#f59e0b',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 1,
  },
  featuredBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#ffffff',
    marginLeft: 4,
  },
  urgentBadge: {
    position: 'absolute',
    top: 12,
    left: 12,
    backgroundColor: '#ef4444',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 1,
  },
  urgentBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#ffffff',
    marginLeft: 4,
  },
  jobCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  companyLogoContainer: {
    marginRight: 12,
  },
  companyLogo: {
    width: 48,
    height: 48,
    borderRadius: 8,
  },
  companyLogoPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: '#0e4e96',
    alignItems: 'center',
    justifyContent: 'center',
  },
  companyLogoPlaceholderText: {
    fontSize: 20,
    fontWeight: '600',
    color: '#ffffff',
  },
  jobCardHeaderInfo: {
    flex: 1,
  },
  jobTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 4,
  },
  companyName: {
    fontSize: 16,
    color: '#64748B',
    marginBottom: 8,
  },
  jobMetadata: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metadataItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
  },
  metadataText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 4,
  },
  saveButton: {
    padding: 8,
  },
  jobCardBody: {
    marginTop: 8,
  },
  jobTags: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  jobTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    marginRight: 8,
    marginBottom: 4,
    flexDirection: 'row',
    alignItems: 'center',
  },
  typeTag: {
    backgroundColor: '#0e4e96' + '20',
  },
  remoteTag: {
    backgroundColor: '#06b6d4' + '20',
  },
  experienceTag: {
    backgroundColor: '#64748B' + '20',
  },
  appliedTag: {
    backgroundColor: '#10b981' + '20',
  },
  jobTagText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#0e4e96',
  },
  jobDescription: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
    marginBottom: 12,
  },
  jobFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  salaryContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  salaryText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#10b981',
    marginLeft: 4,
  },
  matchScoreContainer: {
    alignItems: 'center',
  },
  matchScore: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 2,
  },
  matchScoreText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#ffffff',
  },
  matchLabel: {
    fontSize: 10,
    color: '#64748B',
  },
  applicationStatus: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  statusIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  statusText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  interviewDate: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 22,
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#1f2937',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateMessage: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
  },
  refreshButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
  },
  refreshButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  loadingContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: '#64748B',
    marginTop: 16,
    fontWeight: '500',
  },
});
